import { env } from 'config';

/**
 * Аналитика BI Pulse. Ключ - необязательный PULSE_API_KEY из /env.js;
 * без ключа (например, локально) скрипт не грузится и все вызовы молча пропускаются.
 * Приложение от аналитики не зависит: блокировка скрипта или его ошибки ничего не ломают.
 */

const PULSE_SCRIPT_URL = 'https://pulse-analytics.bi.group/pulse.js';

type PulseProperties = Record<string, unknown>;

interface PulseGlobal {
  identify: (userId: string) => void;
  capture: (event: string, properties?: PulseProperties) => void;
}

// SDK при выполнении кладёт объект в window.pulse (строчными), window.Pulse он не создаёт
const getPulse = (): PulseGlobal | undefined => (globalThis as typeof globalThis & { pulse?: PulseGlobal }).pulse;

// Вызовы до загрузки скрипта копятся здесь и выполняются, как только он загрузится
let pending: Array<(pulse: PulseGlobal) => void> = [];
let isLoaded = false;

const safeCall = (fn: (pulse: PulseGlobal) => void) => {
  const pulse = getPulse();

  if (!pulse) return;

  try {
    fn(pulse);
  } catch (error) {
    // Ошибка аналитики не должна ломать пользовательский сценарий
    console.warn('BI Pulse:', error);
  }
};

const call = (fn: (pulse: PulseGlobal) => void) => {
  if (isLoaded) safeCall(fn);
  else pending.push(fn);
};

export const loadPulse = () => {
  const apiKey = env.PULSE_API_KEY;

  if (!apiKey) return;

  const script = document.createElement('script');
  // apiKey в адресе включает автоинициализацию: SDK сам создаёт window.pulse и сам шлёт page_view на переходах SPA
  script.src = `${PULSE_SCRIPT_URL}?apiKey=${encodeURIComponent(apiKey)}`;
  script.async = true;
  script.onload = () => {
    isLoaded = true;
    pending.forEach(safeCall);
    pending = [];
  };
  // Скрипт заблокирован или недоступен — очередь больше не нужна
  script.onerror = () => {
    pending = [];
  };

  document.head.appendChild(script);
};

export const pulseIdentify = (userId: string) => {
  if (!userId) return;

  call((pulse) => pulse.identify(userId));
};

export const pulseCapture = (event: string, properties?: PulseProperties) => {
  call((pulse) => pulse.capture(event, properties));
};

/** Просмотр экрана: screen - шаблон маршрута (/application/:applicationNumber), url - фактический путь */
export const pulseScreenView = (screen: string, url: string) => {
  pulseCapture('screen_view', { screen, url });
};
